import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { every, isEmpty } from 'underscore';
import SteppedModal from 'components/SteppedModal';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import TwitterPostModal from 'containers/TwitterPostModal';
import { TwitterPostModalSteps } from 'containers/TwitterPostModal/steps/types';
import useAccessDeniedStep from 'containers/TwitterPostModal/steps/useAccessDeniedStep';
import useAuthenticationProgressStep from 'containers/TwitterPostModal/steps/useAuthenticationProgressStep';
import useAuthorizationStep from 'containers/TwitterPostModal/steps/useAuthorizationStep';
import useAutopostingComposerStep from 'containers/TwitterPostModal/steps/useAutopostingComposerStep';
import { UseComposerStepValue } from 'containers/TwitterPostModal/steps/useAutopostingComposerStep/types';
import useSwitchAccountStep from 'containers/TwitterPostModal/steps/useSwitchAccountStep';
import { showError } from 'redux/modules/notification';
import { twitterUserDataSelector } from 'redux/modules/social';
import { TwitterSheetDefaultValues } from './types';

export interface TwitterAutoPostModalProps {}

const ACCESS_DENIED_MESSAGE = 'access_denied';
const ACCESS_DENIED_STEP = 'access-denied';
const AUTHORIZATION_STEP = 'authorization';

const TwitterAutoPostModal: React.FC<TwitterAutoPostModalProps> = () => {
  const [activeStep, setActiveStep] = useState('');

  const modalRef = useRef<SteppedModal>(null);

  const { params, show, onExited, onHide } = useConnectedModal(
    'TwitterAutoPost',
  );

  const dispatch = useDispatch();

  const twitterUserData = useSelector(twitterUserDataSelector);

  const { subscriptionItem } = params ?? {};

  const { description } =
    subscriptionItem?.autoPostVideoPreference.options || {};

  const initialValues = React.useMemo((): TwitterSheetDefaultValues => {
    return {
      description:
        description ??
        'Here’s a new clip from <Episode Title>! Listen to the full episode here: <Link To Episode>',
    };
  }, [description]);

  const replaceModalStep = useCallback((id: TwitterPostModalSteps): void => {
    modalRef.current?.stepHistory.replace(id);
  }, []);

  const handleGoToProgressStep = useCallback(() => {
    replaceModalStep('authentication-in-progress');
  }, [replaceModalStep]);

  const handleSwitchAccount = useCallback(() => {
    replaceModalStep('switch');
  }, [replaceModalStep]);

  const handleGoToComposerStep = useCallback(() => {
    if (every(twitterUserData, isEmpty)) {
      return undefined;
    }

    return replaceModalStep('composer');
  }, [replaceModalStep, twitterUserData]);

  const handleAuthenticationSuccess = useCallback(async () => {
    replaceModalStep('composer');
  }, [replaceModalStep]);

  const handleHide = useCallback(
    (values?: UseComposerStepValue) => {
      handleGoToComposerStep();
      onHide(values);
    },
    [handleGoToComposerStep, onHide],
  );

  const handleAuthorizationError = useCallback(
    (err: Error) => {
      if (err.message === ACCESS_DENIED_MESSAGE) {
        modalRef?.current?.stepHistory.replace(ACCESS_DENIED_STEP);
      } else {
        modalRef?.current?.stepHistory.replace(AUTHORIZATION_STEP);
        onHide();
        dispatch(showError('Error authenticating with X'));
      }
    },
    [dispatch, onHide],
  );

  const handleExited = useCallback(() => {
    handleGoToComposerStep();
    onExited();
  }, [handleGoToComposerStep, onExited]);

  const handleSubmit = useCallback(
    (values: UseComposerStepValue): void => {
      handleHide(values);
    },
    [handleHide],
  );

  const authorization = useAuthorizationStep({
    onAuth: handleGoToProgressStep,
    onAuthSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  const authenticationProgress = useAuthenticationProgressStep();

  const accessDenied = useAccessDeniedStep({
    onError: () => null,
    onReviseAccess: () => null,
  });

  const switchAccount = useSwitchAccountStep({
    onSwitchAccount: handleGoToProgressStep,
    onSwitchAccountSuccess: handleAuthenticationSuccess,
    onError: handleAuthorizationError,
  });

  const composer = useAutopostingComposerStep({
    initialValues,
    onSwitchAccountClick: handleSwitchAccount,
    onHideModal: handleHide,
    onSubmit: handleSubmit,
  });

  const isComposerStep = activeStep === 'composer';

  return (
    <TwitterPostModal
      {...{ show }}
      ref={modalRef}
      title={!isComposerStep && 'auto-post videos'}
      defaultStep={authorization.id}
      steps={[
        authorization,
        authenticationProgress,
        accessDenied,
        switchAccount,
        composer,
      ]}
      className={isComposerStep && 'composer-step-container'}
      onStepChange={setActiveStep}
      onHide={() => handleHide()}
      onExited={handleExited}
    />
  );
};

export default TwitterAutoPostModal;
